import React, { useEffect, useRef, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useContent } from "../context/ContentContext";
import { useEditMode } from "../context/EditModeContext";

/**
 * Libellé CMS pour un bouton/lien CTA : jamais éditable, jamais d'interception de clic.
 * Le copy marketing reste dans EditableText (titres, paragraphes).
 */
export function CtaLabel({ editKey, defaultValue = "", className }) {
  const { getText } = useContent();
  const value = getText(editKey, defaultValue) ?? defaultValue;
  return <span className={className}>{value}</span>;
}

/**
 * Texte éditable en mode admin.
 * Visiteurs / édition OFF : rendu neutre (aucune UI d'édition).
 *
 * @param {string} editKey - clé content (ex. home.hero.title)
 * @param {string} defaultValue
 * @param {'h1'|'h2'|'h3'|'p'|'span'|'div'} as
 * @param {(text: string) => React.ReactNode} [formatDisplay] - rendu enrichi hors édition
 */
export default function EditableText({
  editKey,
  defaultValue = "",
  as: Tag = "span",
  className,
  formatDisplay,
  multiline = false,
  ...rest
}) {
  const { getText, saveText } = useContent();
  const { isEditMode } = useEditMode();
  const value = getText(editKey, defaultValue) ?? "";

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!isEditMode && editing) {
      setEditing(false);
      setDraft(value);
    }
  }, [isEditMode, editing, value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      const len = (inputRef.current.value ?? "").length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  const display = formatDisplay ? formatDisplay(value) : value;

  if (!isEditMode) {
    return (
      <Tag className={className} data-edit-key={editKey} {...rest}>
        {display}
      </Tag>
    );
  }

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const save = async () => {
    const next = draft.trim();
    if (!next) {
      toast.error("Le texte ne peut pas être vide");
      return;
    }
    if (next === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await saveText(editKey, next);
      toast.success("Texte enregistré");
      setEditing(false);
    } catch (err) {
      toast.error(err.message || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      save();
    }
    if (e.key === "Enter" && multiline && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    }
  };

  if (editing) {
    const InputTag = multiline ? "textarea" : "input";
    return (
      <span className="editable-text-wrap editable-text-wrap--editing relative inline-block max-w-full align-top">
        <InputTag
          ref={inputRef}
          className={cn(
            "editable-text-input w-full bg-white border border-indigo-300 rounded px-2 py-1 text-slate-900 font-inherit outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-300/60",
            multiline && "min-h-[4.5rem] resize-y",
            className
          )}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={saving}
          rows={multiline ? 3 : undefined}
          aria-label={`Éditer ${editKey}`}
        />
        <span className="editable-text-actions absolute -bottom-8 left-0 z-[130] flex items-center gap-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium px-2 py-0.5 disabled:opacity-60"
          >
            <Check className="h-3 w-3" aria-hidden="true" />
            Enregistrer
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded bg-white hover:bg-slate-50 text-slate-600 text-[11px] font-medium px-1.5 py-0.5 border border-slate-200"
            title="Échap pour annuler"
          >
            <X className="h-3 w-3" aria-hidden="true" />
            Annuler
          </button>
        </span>
      </span>
    );
  }

  return (
    <Tag
      {...rest}
      className={cn("editable-text", className)}
      data-edit-key={editKey}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setEditing(true);
        }
      }}
      role="button"
      tabIndex={0}
      title="Cliquer pour modifier"
    >
      {display}
      <span className="editable-text-badge" aria-hidden="true">
        <Pencil className="h-2.5 w-2.5" strokeWidth={2} />
      </span>
    </Tag>
  );
}
